import { AreaPathNode } from "./area-path-node";
import { UiControlConstants } from "../../common/uicontrol-constants";
import { AreaPathTree } from "./area-path-tree";
import { RecoverableBinding, recoverableField } from "../../../ServiceDesk/recovery/recoverable-control";
import { LocalizationService } from "../../localization/localization";

export class AreaPathBinding extends RecoverableBinding {
  /** The hierarchical area path data.
   *  The data should be an array of objects where each object represents a Node.
   *  This Node should a contain a field 'title' (display name for the node) and another field 'nodes' (child nodes) which is a an array of Node objects.
   *  [{'title': 'parent', 'sapId': ab1234, 'key': 'value', childNodes : [{'title': 'child1'}, {'title': 'child2'}]}].
   *  You can see the interface at ServiceDesk.Controls.AreaPath.Node
   */
  public data = "<";

  /**  The area path value that is bound on the control */
  public areaPath = "=";

  /** fn called to check if the current node is the node to load the initial area path.
   *  Parameter '{ nodeData: any}' is passed to the function.
   *  Required when there is a need to set the initial load for area path.
   */
  public initialNodePredicate = "&?";

  /** true/false. Whether the control is disabled or enabled. Defaults to false. */
  public isDisabled = "<?";

  /** true/false. Whether the control treats leaf only nodes to be selectable or not. Defaults to false. */
  public selectOnlyLeafNodes = "<?";

  /** fn called when the area path is changed. Parameter '{ isAreaPathInvalid: boolean, areaPath: string, nodeData: any}' is passed to the function*/
  public onAreaPathChange = "&?";

  /** Placeholder text for the dropdown search box */
  public placeholderText = "<?";

  /** The identifier for all business intelligence (BI) actions triggered by this component.*/
  public biId = "@?";

  /** The maximum number of area path nodes to be shown in dropdown. Defaults to 8. */
  public areaPathMaxItemCount = "@?";

  /** The depth layer till which the area path hierarchy will be pre-expanded. Defaults to 1. */
  public expandedDepthLevel = "@?";

  /** Added for accessibility */
  public ariaLabelledby = "@?";
  /** Sets the controls required attribute to true or false. Added for accessibility */
  public ariaRequired = "@?";

  /** Label for dropdown field */
  public label = "@?";

  /** The required-country class is added or removed based on true or false value of requiredCountry respectively*/
  public requiredCountry = "<?";

  /** Set the showValidation value to true or false to show validation. */
  public showValidation = "@?";
}

/**
 *  Interface which represents an abstract representation of a Node. You can use this interface to provide data
 *  to the control.
 *  This Node should a contain a field 'title' (display name for the node) and another field 'nodes' (child nodes) which is a an array of Node objects.
 *  [{'title': 'parent', 'sapId': ab1234, 'key': 'value', childNodes : [{'title': 'child1'}, {'title': 'child2'}]}].
 */
export interface Node {
  /**
   * The children for the current node.
   */
  childNodes?: Node[];
  /**
   * The title (display name) for the current node.
   */
  title: string;

  /** unique identifier required for recovery */
  id?: string;
}

const defaultExpandedDepthLevel = 1;
const defaultAreaPathMaxItemCount = 8;

export class AreaPathController {
  public data: Node[];
  public onAreaPathChange: any;
  public isInputFocused = false;
  private isAreaPathInvalid: boolean;
  @recoverableField
  private selectedNodeId: string;
  public areaPath: string;
  public showDropDown: boolean;
  public selectedNodeIndex: number;
  public areaPathTree: AreaPathTree;
  private mousePositionPageY = 0;
  public selectedRangeLength = 0;
  public selectedRangeKey: string;
  private doNotAutoComplete = false;
  public biId: string;
  public label: string;
  public requiredCountry = false;
  public showValidation = false;
  public initialNodePredicate: any;
  public selectOnlyLeafNodes: boolean;
  public selectedNodeData: any;
  public areaPathMaxItemCount = defaultAreaPathMaxItemCount;
  public expandedDepthLevel: string;
  public parsedExpandedDepthLevel: number;
  public ariaRequired: boolean;
  // used to set scroll position explicitly owing to virtualization.
  public scrollToIndex: number;
  // used to identify if the user is in search context.
  private inSearchContext = false;
  private nodeSelectedByArrowKeys = false;
  // @ngInject
  constructor(
    private $scope: ng.IScope,
    private $element: ng.IAugmentedJQuery,
    private uiControlConstants: UiControlConstants,
    private sdLocalizationService: LocalizationService
  ) {
    this.parsedExpandedDepthLevel = this.expandedDepthLevel
      ? parseInt(this.expandedDepthLevel)
      : defaultExpandedDepthLevel;
    this.areaPathTree = new AreaPathTree(
      this.data,
      this.selectOnlyLeafNodes,
      this.initialNodePredicate,
      this.parsedExpandedDepthLevel
    );
  }

  public $onChanges(changesObj: any) {
    if (changesObj.data) {
      this.areaPathTree = new AreaPathTree(
        this.data,
        this.selectOnlyLeafNodes,
        this.initialNodePredicate,
        this.parsedExpandedDepthLevel
      );
      this.refreshView();
    }
  }

  public $postLink() {
    this.areaPathTree.updateFocusedNodeOnLoad(this.areaPath);
    this.scrollToIndex = this.areaPathTree.getSelectedNodeIndex();
  }

  private refreshView() {
    this.areaPathTree.updateFocusedNodeOnLoad(this.areaPath);
    // update areaPath only when using nodeData with initialNodePredicate
    if (this.initialNodePredicate) {
      this.areaPath = this.areaPathTree.getAreaPath();
    }

    // update area path validity only if areaPath is defined and it exists
    if (this.areaPath) {
      this.isAreaPathInvalid = this.areaPathTree.getAreaPathValidity();
    }
  }

  $sdRecoveryOnComplete(data: any) {
    this.selectNode(
      undefined,
      this.areaPathTree.getTreeData().find((td) => td.nodeData && td.nodeData.id == data.selectedNodeId)
    );
  }

  $sdRecoveryIsModelValid(data: any) {
    return (
      data.selectedNodeId &&
      this.areaPathTree.getTreeData().find((td) => td.nodeData && td.nodeData.id == data.selectedNodeId)
    );
  }

  public navigateTree($event: any) {
    switch ($event.keyCode) {
      case this.uiControlConstants.keyCode.enterKeyCode:
        let node = this.areaPathTree.getFocusedNode();
        this.selectNode($event, node);
        break;

      case this.uiControlConstants.keyCode.rightArrowKeyCode:
        this.areaPathTree.expandNode();
        break;

      case this.uiControlConstants.keyCode.downArrowKeyCode:
        this.areaPathTree.navigateToNextNode();
        this.UpdateAreaPathAndSelectNode(false);
        this.nodeSelectedByArrowKeys = true;
        break;

      case this.uiControlConstants.keyCode.upArrowKeyCode:
        this.areaPathTree.navigateToPreviousNode();
        this.UpdateAreaPathAndSelectNode(false);
        this.nodeSelectedByArrowKeys = true;
        break;

      case this.uiControlConstants.keyCode.backspaceKeyCode:
      case this.uiControlConstants.keyCode.deleteKeyCode:
        this.updateAreaPathValidity(false, true);
        this.doNotAutoComplete = true;
        break;

      default:
        this.doNotAutoComplete = false;

        this.inSearchContext = true;
        this.showDropDown = true;
    }

    // let the event propagate to onChange event when the keyCode is not any one of below
    if (
      $event.keyCode == this.uiControlConstants.keyCode.enterKeyCode ||
      $event.keyCode == this.uiControlConstants.keyCode.leftArrowKeyCode ||
      $event.keyCode == this.uiControlConstants.keyCode.downArrowKeyCode ||
      $event.keyCode == this.uiControlConstants.keyCode.upArrowKeyCode
    ) {
      $event.preventDefault();
    }
  }

  public UpdateAreaPathAndSelectNode(updateSubscribers = true) {
    this.areaPath = this.areaPathTree.getAreaPath();
    this.updateSelectedNode();
    if (this.selectOnlyLeafNodes && this.areaPathTree.getFocusedNode().children.length > 0) {
      return;
    } else {
      this.updateAreaPathValidity(false, false, updateSubscribers);
    }
  }

  public showValidationMsg(id: string) {
    let errorMessageElement = document.getElementById("errormessage-" + id);
    let inputElement: any = document.getElementById("input-" + id);
    if (this.showValidation) {
      if (
        this.ariaRequired &&
        inputElement &&
        angular.element(inputElement).hasClass("incorrect-path") &&
        inputElement.value.length > 0
      ) {
        this.updateErrorMessage(
          id,
          this.sdLocalizationService.getLocalizedString("customershell", "_Input_Vaue_Invalid_")
        );
      } else if (this.ariaRequired && inputElement && inputElement.value.length <= 0) {
        this.updateErrorMessage(
          id,
          this.label + " " + this.sdLocalizationService.getLocalizedString("customershell", "_Input_Field_Required_")
        );
      } else {
        this.requiredCountry = false;
        if (errorMessageElement) {
          errorMessageElement.style.display = "none";
        }
      }
    }
  }

  private updateErrorMessage(id: string, errorMessage: string) {
    let errorMessageElement = document.getElementById("errormessage-" + id);
    if (errorMessageElement) {
      this.requiredCountry = true;
      errorMessageElement.style.display = "block";
      errorMessageElement.style.fontSize = "12px";
      errorMessageElement.innerHTML = "<span></span>";
      errorMessageElement.innerHTML = errorMessage;
    }
  }

  public selectNode(event: any, node: AreaPathNode) {
    // if selectOnlyLeafNodes set to true, only leafnodes are allowed to be selectable else every node can be selected as path.
    if (this.selectOnlyLeafNodes && node.children.length > 0) {
      this.toggleAndFocus(node);
    } else {
      this.areaPathTree.applyFocus(node, true);
      this.areaPath = this.areaPathTree.constructAreaPath(node);
      this.setDefaultState();
      this.updateSelectedNode();
      this.updateSubscribers();
      this.showDropDown = false;
      this.areaPathTree.removeFocus();
      this.updateAreaPathValidity(false, false);
      this.$scope.$emit("updateAreaPathTelemetry");
    }
    let hiddenLabel = document.getElementsByName("lblCountryHidden");
    if (hiddenLabel && hiddenLabel.length > 0) {
      let hiddenLabelId: any = hiddenLabel.item(hiddenLabel.length - 1).id;
      setInterval(() => this.showValidationMsg(hiddenLabelId), 100);
    }
  }

  public toggleCollapsedState(event: any, node: AreaPathNode) {
    this.toggleAndFocus(node);
    this.areaPath = this.areaPathTree.constructAreaPath(node);
    this.updateSelectedNode();
    this.updateAreaPathValidity(false, false);

    event.stopPropagation();
    event.preventDefault();
  }

  public toggleAndFocus(node: AreaPathNode) {
    this.areaPathTree.toggleVisibilityChildNodes(node, node.isCollapsed);
    node.isCollapsed = !node.isCollapsed;
    this.isInputFocused = true;
    this.areaPathTree.applyFocus(node, true);
  }

  public onChange($event: any) {
    this.areaPathTree.removeFocus();
    let isUserSearching = false;
    if (!this.areaPath.includes("\\") && this.inSearchContext) {
      isUserSearching = true;
    }
    let focusedNode = this.areaPathTree.updateFocusedNodeAreaPath(
      this.areaPath,
      !this.doNotAutoComplete,
      isUserSearching
    );

    this.areaPathTree.applyFocus(focusedNode, true);

    if (!this.doNotAutoComplete && focusedNode && !isUserSearching) {
      // autocomplete scenario
      this.areaPath = this.areaPathTree.getAreaPath();
      this.updateSelectedNode();
      this.selectedRangeLength = this.areaPathTree.selectedRange;
      this.selectedRangeKey = `${this.areaPath || ""}_${(this.selectedNodeIndex || 0).toString()}_${(
        this.selectedRangeLength || 0
      ).toString()}`;
    }

    if (isUserSearching) {
      // search scenario
      this.setDefaultState();
      // context is set to search
      this.inSearchContext = true;
    }

    this.updateAreaPathValidity(true, undefined);
    this.updateSubscribers();
  }

  public onFocusLost($event: any) {
    if (this.nodeSelectedByArrowKeys) {
      try {
        this.updateSubscribers();
      } finally {
        this.nodeSelectedByArrowKeys = false;
      }
    }
  }

  public onClickedOutside() {
    this.showDropDown = false;
    this.setDefaultState();
    this.$scope.$emit("updateAreaPathTelemetry");
  }

  public clearSelection($event: any) {
    this.areaPath = "";
    this.onChange($event);
    // search context reset to false on clearing the area path or searchText
    this.inSearchContext = false;
    this.setDefaultState();
    this.isInputFocused = true;
  }

  public onHover($event: any, node: AreaPathNode) {
    // when a node is collapsed or expanded, hover is triggered for a new node that might have the focus of mouse pointer. This check prevents that.
    if ($event.pageY == this.mousePositionPageY) {
      return;
    }

    this.mousePositionPageY = $event.pageY;
    this.areaPathTree.removeFocus();
    this.areaPathTree.applyFocus(node, false);
  }

  private updateSelectedNode() {
    this.selectedNodeIndex = this.areaPathTree.getSelectedNodeIndex();
    this.selectedNodeData = this.areaPathTree.getSelectedNodeData();
    this.selectedNodeId = this.selectedNodeData && this.selectedNodeData.id;
  }

  public updateFocusedNode($event: any, showDropDown: boolean) {
    this.isInputFocused = false;
    this.areaPathTree.updateFocusedNode(this.areaPath);
    this.updateSelectedNode();
    this.showDropDown = showDropDown;
    if (this.areaPath) {
      // if user is searching,
      this.scrollToIndex = this.inSearchContext
        ? this.areaPathTree.getSelectedNodeIndexInMatchedNodes()
        : this.selectedNodeIndex;
    } else {
      // when area path is null, the scroll should be at top or 0.
      this.scrollToIndex = 0;
    }
  }

  public toggleDropDownVisibility($event: any) {
    if (!this.showDropDown) {
      this.updateFocusedNode($event, true);
    } else {
      this.setDefaultState();
      this.showDropDown = false;
    }
  }

  public hasShownItems() {
    return !this.areaPathTree.getTreeData().every((o: AreaPathNode) => o.isVisible == false);
  }

  public filteredAreaPathItems() {
    return this.areaPathTree.getTreeData().filter((i) => i.isVisible);
  }

  public areaPathItemHeight(): number {
    let itemHeight = this.itemHeight();
    return this.countOfItemsToShow() * itemHeight;
  }

  public itemHeight(): number {
    return this.$element.find(".node-item").outerHeight();
  }

  public countOfItemsToShow(): number {
    let showableItemCount = 0;
    let nodeData = this.areaPathTree.getTreeData();
    for (let i = 0; i < nodeData.length && showableItemCount < this.areaPathMaxItemCount; ++i) {
      if (nodeData[i].isVisible != false) {
        showableItemCount++;
      }
    }
    return showableItemCount;
  }

  // called when drop down is closed / area path node selected / search text is cleared.
  // sets the scroll index and selected node index to 0/undefined,
  // so that they can be reset when dropdown is displayed or input is focused and correct focus is set on node.
  private setDefaultState() {
    this.scrollToIndex = 0;
    this.selectedNodeIndex = undefined;
  }

  private updateAreaPathValidity(get: boolean, valid: boolean, updateSubscribers = true): void {
    this.isAreaPathInvalid = get
      ? this.areaPathTree.getAreaPathValidity()
      : this.areaPathTree.setAreaPathValidity(valid);
    if (updateSubscribers) {
      this.updateSubscribers();
    }
  }

  private updateSubscribers(): void {
    if (this.onAreaPathChange) {
      this.onAreaPathChange({
        isAreaPathInvalid: this.isAreaPathInvalid,
        areaPath: this.areaPath,
        nodeData: this.selectedNodeData
      });
    }
  }
}
export const sdAreaPath: ng.IComponentOptions = {
  bindings: <any>new AreaPathBinding(),
  controller: AreaPathController,
  templateUrl: "../ShellCommon/Controls/area-path/area-path.html"
};
